import React, { useState, useEffect, useRef } from 'react'
import { css } from '@emotion/react'
import ReactDOM from 'react-dom'
import Highlighter from 'react-highlight-words'
import { checkIsSafari } from '@/utils/checkIsSafari'

interface Props {
  text: string
  search?: string
  width?: string | number
  variableColor? : string
}

export const TableHighlightWrapper = ({
  variableColor,
  text,
  search = '',
  width = 200,
}: Props) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const [shouldShowTooltip, setShouldShowTooltip] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const spanRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (containerRef.current && spanRef.current && text.trim() !== '') {
      const containerWidth = containerRef.current.clientWidth
      const textWidth = spanRef.current.scrollWidth
      const isOverflowing = textWidth > containerWidth + 1

      setShouldShowTooltip(isOverflowing)
    } else {
      setShouldShowTooltip(false)
    }
  }, [text, search, width])

  const handleMouseEnter = () => {
    if (
      !checkIsSafari() &&
      shouldShowTooltip &&
      containerRef.current &&
      text.trim() !== ''
    ) {
      const rect = containerRef.current.getBoundingClientRect()
      setPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      })
      setShowTooltip(true)
    }
  }

  const handleMouseLeave = () => {
    setShowTooltip(false)
  }

  return (
    <>
      <div
        css={baseStyle(width)}
        ref={containerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="text__holder">
          <span ref={spanRef} className="ellipsis-text">
            {search ? (
              <Highlighter
                textToHighlight={text}
                searchWords={[search]}
                autoEscape
                highlightStyle={{
                  backgroundColor: variableColor || '#ffc069',
                }}
              />
            ) : (
              text
            )}
          </span>
        </div>
      </div>

      {showTooltip &&
        ReactDOM.createPortal(
          <div
            className="full-text-popup"
            css={popupStyle}
            style={{ top: position.top, left: position.left }}
          >
            {text}
          </div>,
          document.body
        )}
    </>
  )
}

const baseStyle = (width: string | number) => css({
    position: 'relative',
    cursor: 'default',

    '.text__holder': {
      maxHeight: '40px',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      width,
      maxWidth: width,
    },

    '.ellipsis-text': {
      display: 'inline-block',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      maxWidth: '94%',
      verticalAlign: 'bottom',
    },
  })

const popupStyle = css({
  position: 'absolute',
  zIndex: 10000,
  background: 'white',
  marginTop: '-1px',
  marginLeft: '5px',
  padding: '5px 10px',
  boxShadow: '2px 4px 7px 0px rgba(0, 0, 0, 0.15)',
  border: '1px solid #ccc',
  maxWidth: '1000px',
  whiteSpace: 'normal',
  wordBreak: 'break-word',

  '@media(max-width: 1150px)': {
    maxWidth: '265px !important',
  },

  color: '#000',
  fontFamily: "Open Sans",
  fontSize: '13px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 'normal',
})
